// src/pages/Game/GameSelectTest.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import API from "../../api";

export default function GameSelectTest() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(""); // ✅ SEARCH STATE

  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const fetchTests = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const endpoint = token ? "/publictests/all" : "/publictests/public";

        const res = await API.get(endpoint, { headers });
        if (!mounted) return;

        if (Array.isArray(res.data)) {
          setTests(res.data);
        } else if (res.data && Array.isArray(res.data.tests)) {
          setTests(res.data.tests);
        } else {
          setTests([]);
        }
      } catch (err) {
        if (mounted) setError("Failed to fetch tests from server");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchTests();
    return () => (mounted = false);
  }, []);

  // ✅ FILTERED TESTS
  const filteredTests = tests.filter((test) =>
    test.title?.toLowerCase().includes(search.toLowerCase())
  );

  /* ================= STATES ================= */
  if (loading)
    return (
      <p className="text-center mt-16 text-[#5C4632]">
        ⏳ Loading available tests...
      </p>
    );

  if (error)
    return (
      <p className="text-center mt-16 text-red-600 font-medium">
        {error}
      </p>
    );

  return (
    <div className="min-h-screen bg-[#FAF6EF] px-6 py-16 text-[#3A2A1A]">

      {/* ================= HEADER ================= */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-4xl mx-auto text-center mb-10"
      >
        <h1 className="text-4xl font-bold mb-3">
          🎮 Choose a Test to Play
        </h1>
        <p className="text-[#5C4632] text-lg">
          Select a test and challenge yourself through interactive gameplay.
        </p>
      </motion.div>

      {/* ================= SEARCH ================= */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="max-w-md mx-auto mb-12"
      >
        <input
          type="text"
          placeholder="Search tests by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-4 py-3 rounded-xl
                     border border-[#E6D8C3]
                     bg-[#F4EBDD]
                     text-[#3A2A1A]
                     placeholder-[#7A4A28]/60
                     focus:outline-none
                     focus:ring-2 focus:ring-[#7A4A28]/40"
        />
      </motion.div>

      {/* ================= TEST LIST ================= */}
      <div className="max-w-4xl mx-auto space-y-6">
        {filteredTests.length === 0 ? (
          <p className="text-center text-[#5C4632]">
            No matching tests found.
          </p>
        ) : (
          filteredTests.map((test, index) => (
            <motion.div
              key={test._id || test.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-[#F4EBDD] border border-[#E6D8C3]
                         rounded-2xl p-6 shadow-md
                         flex flex-col sm:flex-row
                         sm:items-center sm:justify-between
                         gap-4"
            >
              {/* LEFT */}
              <div>
                <h2 className="text-xl font-semibold mb-1">
                  {test.title}
                </h2>
                <p className="text-sm text-[#5C4632]">
                  {Array.isArray(test.questions)
                    ? test.questions.length
                    : test.questions || 0}{" "}
                  Questions
                </p>
              </div>

              {/* RIGHT */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() =>
                  navigate(`/game/play/${test._id || test.id}`)
                }
                className="px-6 py-2 rounded-xl
                           bg-[#7A4A28] text-white
                           font-medium shadow
                           hover:bg-[#5C4632] transition"
              >
                Play Now →
              </motion.button>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}
